import api from './api';
import { setUserContext, addBreadcrumb, logger } from '../utils/sentryUtils';

class AuthService {
    
    // Token storage keys
    ACCESS_TOKEN_KEY = 'accessToken';
    REFRESH_TOKEN_KEY = 'refreshToken';
    USER_KEY = 'user';
    
    async requestAuthCode(email, loginType, householdCode = null) {
        try {
            addBreadcrumb("Auth code requested", "auth", { email, loginType, householdCode });
            logger.info("Requesting authentication code", { email, loginType, householdCode });
            
            const response = await api.post('/auth/request-code', {
                email,
                loginType,
                householdCode
            });
            return response.data;
        } catch (error) {
            logger.error("Error requesting auth code", { email, loginType, error: error.message });
            throw error;
        }
    }
    
    async verifyAuthCode(email, code, loginType, householdCode = null) {
        try {
            addBreadcrumb("Verifying auth code", "auth", { email, loginType, householdCode });
            logger.info("Verifying authentication code", { email, loginType, householdCode });
            
            const response = await api.post('/auth/verify-code', {
                email,
                code,
                loginType,
                householdCode
            });
            
            // If authentication is successful, store tokens and user data
            if (response.data.success) {
                logger.info("Authentication successful", { email, loginType, userId: response.data.user?.id });
                this.storeTokens(response.data.accessToken, response.data.refreshToken);
                this.storeUser(response.data.user);
                
                // Set Sentry user context
                this.setSentryUserContext(response.data.user);
                
                addBreadcrumb("User authenticated", "auth", { 
                    userId: response.data.user?.id, 
                    email, 
                    loginType,
                    userRole: response.data.user?.role
                });
            } else {
                logger.warn("Authentication failed", { email, loginType, message: response.data.message });
            }
            
            return response.data;
        } catch (error) {
            logger.error("Error verifying auth code", { email, loginType, error: error.message });
            throw error;
        }
    }
    
    async refreshToken() {
        try {
            const refreshToken = this.getRefreshToken();
            if (!refreshToken) {
                throw new Error('No refresh token available');
            }
            
            addBreadcrumb("Refreshing token", "auth");
            logger.debug("Refreshing authentication token");
            
            const response = await api.post('/auth/refresh', null, {
                headers: {
                    'Authorization': `Bearer ${refreshToken}`
                }
            });
            
            if (response.data.success) {
                this.storeTokens(response.data.accessToken, response.data.refreshToken);
                logger.info("Token refreshed successfully");
                return response.data.accessToken;
            } else {
                throw new Error('Token refresh failed');
            }
        } catch (error) {
            logger.error("Error refreshing token", { error: error.message });
            this.logout();
            throw error;
        }
    }
    
    // Token management methods
    storeTokens(accessToken, refreshToken) {
        localStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
        localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    }
    
    getAccessToken() {
        return localStorage.getItem(this.ACCESS_TOKEN_KEY);
    }
    
    getRefreshToken() {
        return localStorage.getItem(this.REFRESH_TOKEN_KEY);
    }
    
    removeTokens() {
        localStorage.removeItem(this.ACCESS_TOKEN_KEY);
        localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    }
    
    // User management methods
    storeUser(user) {
        localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    }
    
    getUser() {
        const userStr = localStorage.getItem(this.USER_KEY);
        return userStr ? JSON.parse(userStr) : null;
    }
    
    removeUser() {
        localStorage.removeItem(this.USER_KEY);
    }
    
    // Set Sentry user context
    setSentryUserContext(user) {
        if (user) {
            setUserContext({
                id: user.id,
                email: user.email,
                username: user.preferredName || user.firstName || user.email,
                // Add additional context
                firstName: user.firstName,
                lastName: user.lastName,
                preferredName: user.preferredName,
                roleIds: user.roleIds,
                householdIds: user.householdIds
            });
            
            logger.info("Sentry user context set", { 
                userId: user.id, 
                email: user.email, 
                username: user.preferredName || user.firstName || user.email 
            });
        } else {
            setUserContext(null);
            logger.info("Sentry user context cleared");
        }
    }
    
    // Authentication state methods
    isAuthenticated() {
        const token = this.getAccessToken();
        if (!token) return false;
        
        // Check if token is expired (basic check)
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload.exp * 1000 > Date.now();
        } catch (error) {
            logger.error("Error parsing token", { error: error.message });
            return false;
        }
    }
    
    // Logout method
    logout() {
        addBreadcrumb("User logging out", "auth");
        logger.info("User logging out");
        
        // Clear Sentry user context
        this.setSentryUserContext(null);
        
        this.removeTokens();
        this.removeUser();
        
        // Redirect to login page or clear any other state
        window.location.href = '/login';
    }
    
    // Get authorization header for API requests
    getAuthHeader() {
        const token = this.getAccessToken();
        return token ? { 'Authorization': `Bearer ${token}` } : {};
    }
    
    // Initialize Sentry user context from stored user data
    initializeSentryContext() {
        const user = this.getUser();
        if (user && this.isAuthenticated()) {
            this.setSentryUserContext(user);
        }
    }
}

export default new AuthService(); 